import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { Checkbox, Empty, Radio, Spin } from "antd";
import apiClient from "../../../../../helpers/apiClient";
import InsuranceOptionCard from "../../../../addCoverage/components/InsuranceOptionCard";
import AxleInsuranceCard from "../../../../addCoverage/components/AxleInsuranceCard";

/**
 * "Select insurance" step for the car swap flow.
 *
 * Mirrors the booking site's insurance-selection step used on the "complete reservation"
 * page (InsuranceAddons.tsx): pick a paid insurance add-on, verify a personal policy via
 * Axle, or explicitly continue without protection. One of these three choices is required
 * before the swap can proceed — see `onSelectionStateChange` consumed by CarSwapModal.
 *
 * The swapped-in car may have a different insurance catalog (or none at all) than the car
 * being replaced, so this step always starts from a blank slate — the admin makes a fresh,
 * explicit choice for the new car rather than inheriting the old selection.
 */
const CarSwapInsuranceStep = ({ carId, pickupLocationId, reservation, onSelectionStateChange, t }) => {
    const [insuranceAddonsData, setInsuranceAddonsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAxleCard, setShowAxleCard] = useState(false);
    const [axleTokenData, setAxleTokenData] = useState(null);
    const [isAxleVerifying, setIsAxleVerifying] = useState(false);
    const [removeAxleVerification, setRemoveAxleVerification] = useState(false);
    const [selectedInsuranceId, setSelectedInsuranceId] = useState(null);
    const [noProtection, setNoProtection] = useState(false);

    const isAxleOptionSelected = Boolean(showAxleCard || axleTokenData);

    const fetchInsuranceAddons = useCallback(async () => {
        if (!carId || !pickupLocationId) {
            setInsuranceAddonsData([]);
            return;
        }

        setLoading(true);
        try {
            const { data } = await apiClient.get(
                `/api/addons-insurance/available?carId=${carId}&pickupLocationId=${pickupLocationId}`,
            );
            setInsuranceAddonsData(data?.success && data?.data ? data.data.result || [] : []);
        } catch {
            setInsuranceAddonsData([]);
        } finally {
            setLoading(false);
        }
    }, [carId, pickupLocationId]);

    useEffect(() => {
        fetchInsuranceAddons();
    }, [fetchInsuranceAddons]);

    // A different car may have a different set of available insurance products, so any
    // selection made for the previous car is no longer valid and must be cleared.
    useEffect(() => {
        setSelectedInsuranceId(null);
        setShowAxleCard(false);
        setAxleTokenData(null);
        setNoProtection(false);
        setRemoveAxleVerification(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carId]);

    useEffect(() => {
        onSelectionStateChange({
            insuranceAddonsData,
            selectedInsuranceId,
            axleTokenData,
            isAxleVerifying,
            isAxleOptionSelected,
            noProtection,
        });
    }, [
        axleTokenData,
        insuranceAddonsData,
        isAxleOptionSelected,
        isAxleVerifying,
        noProtection,
        onSelectionStateChange,
        selectedInsuranceId,
    ]);

    const handleSelectInsurance = (addonId) => {
        if (noProtection) return;
        setSelectedInsuranceId(addonId);
        setRemoveAxleVerification(true);
        setAxleTokenData(null);
        setShowAxleCard(false);
    };

    const handleToggleOwnInsurance = () => {
        if (noProtection) return;
        const next = !showAxleCard;
        setShowAxleCard(next);
        if (!next) {
            setAxleTokenData(null);
            setRemoveAxleVerification(true);
        } else {
            setSelectedInsuranceId(null);
        }
    };

    const handleAxleTokenData = (data) => {
        setAxleTokenData(data);
        if (data) {
            setSelectedInsuranceId(null);
        }
    };

    const handleToggleNoProtection = () => {
        const next = !noProtection;
        setNoProtection(next);
        if (next) {
            setSelectedInsuranceId(null);
            setShowAxleCard(false);
            setAxleTokenData(null);
            setRemoveAxleVerification(true);
        }
    };

    const hasInsuranceOptions = insuranceAddonsData.length > 0;
    const insuranceDisabled = noProtection;

    return (
        <section className="flex flex-col gap-4 pb-1">
            <div>
                <h2 className="fontSemi text-xl text-[#222222] md:text-[22px]">{t("chooseCoverageTitle")}</h2>
                <p className="fontRegular mt-2 text-sm leading-6 text-[#7F8796]">{t("chooseCoverageSubtitle")}</p>
            </div>

            {loading ? (
                <div className="flex justify-center py-8">
                    <Spin />
                </div>
            ) : !hasInsuranceOptions ? (
                <Empty description={t("noInsuranceOptions")} />
            ) : (
                <div className="space-y-3">
                    <div
                        className={`w-full rounded-xl border transition-colors ${
                            showAxleCard || axleTokenData
                                ? "border-[#5D5DF6] bg-[#5D5DF6]/[0.03]"
                                : "border-[#E6EAF2] bg-white"
                        } ${insuranceDisabled ? "cursor-not-allowed opacity-50" : ""}`}>
                        <button
                            type="button"
                            disabled={insuranceDisabled}
                            onClick={handleToggleOwnInsurance}
                            className="flex w-full items-start gap-3 p-4 text-left">
                            <Radio checked={Boolean(showAxleCard || axleTokenData)} className="!mt-0.5 !shrink-0" />
                            <div className="min-w-0 flex-1">
                                <p className="fontSemi text-base text-[#222222]">{t("ownInsuranceTitle")}</p>
                                <p className="fontRegular mt-1 text-sm text-[#7F8796]">{t("ownInsuranceHint")}</p>
                            </div>
                        </button>

                        {showAxleCard || axleTokenData ? (
                            <div className="border-t border-[#EEF0F6] px-4 py-4">
                                <AxleInsuranceCard
                                    reservation={reservation}
                                    disabled={insuranceDisabled}
                                    isAxleVerifying={isAxleVerifying}
                                    setIsAxleVerifying={setIsAxleVerifying}
                                    onTokenData={handleAxleTokenData}
                                    removeVerification={removeAxleVerification}
                                    onRemoveVerificationHandled={() => setRemoveAxleVerification(false)}
                                    t={t}
                                />
                            </div>
                        ) : null}
                    </div>

                    {insuranceAddonsData.map((item) => (
                        <InsuranceOptionCard
                            key={item.id}
                            item={item}
                            selected={selectedInsuranceId === item.id}
                            disabled={insuranceDisabled}
                            onSelect={() => {
                                if (selectedInsuranceId === item.id) {
                                    setSelectedInsuranceId(null);
                                } else {
                                    handleSelectInsurance(item.id);
                                }
                            }}
                            t={t}
                        />
                    ))}
                </div>
            )}

            <button
                type="button"
                onClick={handleToggleNoProtection}
                className={`w-full rounded-xl border p-4 text-left transition-colors ${
                    noProtection
                        ? "border-[#FAAD14] bg-[#FFFBE6]"
                        : "border-[#E6EAF2] bg-white hover:border-[#FAAD14]/50"
                }`}>
                <div className="flex items-start gap-3">
                    <Checkbox checked={noProtection} className="!mt-0.5" />
                    <div>
                        <p className="fontSemi text-base text-[#222222]">{t("noProtectionTitle")}</p>
                        <p className="fontRegular mt-1 text-sm leading-6 text-[#7F8796]">{t("noProtectionText")}</p>
                    </div>
                </div>
            </button>
        </section>
    );
};

CarSwapInsuranceStep.propTypes = {
    carId: PropTypes.string,
    pickupLocationId: PropTypes.string,
    reservation: PropTypes.object,
    onSelectionStateChange: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

export default CarSwapInsuranceStep;
